import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Microphone,
  User as UserIcon,
  Target,
  Newspaper,
  EnvelopeSimple,
  DownloadSimple,
  MonitorPlay,
  Layout,
  ChatsCircle,
  Sparkle,
  SpeakerHigh,
  SpeakerSlash,
  LockSimple,
} from "@phosphor-icons/react";
import AgentCard from "../components/AgentCard";
import CardConfettiCannon from "../components/CardConfettiCannon";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isCelebrationSoundOn, setCelebrationSoundOn } from "../lib/sound";
import { useAgentVisibility } from "../lib/agentVisibility";
import { toast } from "sonner";

const EMPTY_STATUS = {
  voice: { generated: false, saved: false },
  client: { generated: false, saved: false },
  offer: { generated: false, saved: false },
};

function hasLocalDraft(agentKey) {
  try {
    const raw = localStorage.getItem(`mex.draft.${agentKey}`);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return false;
    const hasValues =
      parsed.values && typeof parsed.values === "object" && Object.keys(parsed.values).length > 0;
    const hasContent =
      parsed.contentState &&
      ((typeof parsed.contentState.paste === "string" && parsed.contentState.paste.trim()) ||
        (Array.isArray(parsed.contentState.urls) && parsed.contentState.urls.length > 0));
    return Boolean(hasValues || hasContent);
  } catch {
    return false;
  }
}

function cardStatus(agentKey, s) {
  if (s?.saved) return "saved";          // DONE - green pill, purple bg
  if (s?.generated) return "generated";  // DRAFT - server-side blueprint, not saved
  if (hasLocalDraft(agentKey)) return "generated"; // DRAFT - just typing locally
  return "ready";                         // ÜRES
}

export default function Dashboard() {
  const { user, effectiveRole, previewAsTester } = useAuth();
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [celebrateKey, setCelebrateKey] = useState(null);
  const [soundOn, setSoundOn] = useState(isCelebrationSoundOn());
  const { lookup: agentStatusFor } = useAgentVisibility();

  // Admin teljes bypass: a `isAdmin` flag-et a teljes komponens scope-ban
  // használjuk — sem flag-lock (admin_only/hidden), sem progress-lock
  // (Foundation prereq / FoundationComplete) nem fut rá Tamásra. Így
  // a teljes appot szerkesztheti / tesztelheti. A `effectiveRole`-t
  // használjuk, NEM a DB-beli user.role-t — így a „Tester nézet" preview
  // mód továbbra is működik (admin nézet → tester nézet → vissza).
  const isAdmin = effectiveRole === "admin";

  /**
   * Decorate an AgentCard prop bag with admin-only-tease overrides:
   *  - hidden  → returns null  (don't render at all)
   *  - admin_only + tester role → status='admin-only-tease' + locked click toast
   *  - live or admin user → pass through unchanged
   *
   * FONTOS prioritás: ha a kártya már `locked-prereq` állapotban van
   * (progress-lock: a user még nem nyitotta meg a layert), akkor a
   * progress-lock GYŐZ az admin-only-tease felett. A „Hamarosan" pill
   * csak olyan kártyán jelenhet meg, amit a user már elérne a saját
   * progresszióján — de az adott agent admin oldalon még admin_only.
   */
  const decorate = (frontendKey, props) => {
    const flagStatus = agentStatusFor(frontendKey);
    if (flagStatus === "hidden" && !isAdmin) return null; // tester can't see it
    if (flagStatus === "admin_only" && !isAdmin && props.status !== "locked-prereq") {
      return {
        ...props,
        status: "admin-only-tease",
        onLockedClick: () => toast.info("Ez az ügynök hamarosan elérhető lesz."),
      };
    }
    return props;
  };

  // Refs to each Foundation card wrapper — used to anchor the cannon at
  // the BOTTOM edge of the just-saved card.
  const cardRefs = useRef({ voice: null, client: null, offer: null });

  const loadStatus = async () => {
    // Tester preview módban a Dashboard SOHA nem kérdezi le Tamás admin
    // adatait — null állapotú dashboard jelenik meg (csak a Márkahang
    // Varázsló aktív). Így az admin Identitás / Aktív alap / mentett
    // riportok NEM látszanak a preview-ban.
    if (previewAsTester) {
      setStatus(EMPTY_STATUS);
      return;
    }
    try {
      const res = await api.get("/foundation/status");
      setStatus(res.data);
    } catch {}
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewAsTester]);

  useEffect(() => {
    // The Varázsló save flow redirects here with ?celebrate={key} on success.
    // Refresh status so the DONE pill is rendered FIRST, then fire the cannon
    // 200ms later (per spec). The flag is stripped from the URL only AFTER
    // the full TA-TA-TAMMM cycle + particle drop (~3s) so the React re-render
    // triggered by `params` change does NOT eat frames mid-celebration.
    const key = params.get("celebrate");
    if (key && ["voice", "client", "offer"].includes(key)) {
      loadStatus();
      const t = setTimeout(() => setCelebrateKey(key), 200);
      const t2 = setTimeout(() => navigate("/dashboard", { replace: true }), 3000);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setCelebrationSoundOn(next);
  };

  const foundationAgents = [
    {
      key: "voice",
      seq: 1,
      title: "Márkahang Varázsló",
      description: "Fedezd fel a saját, autentikus márkahangod",
      icon: Microphone,
      to: "/agents/voice",
      status: cardStatus("voice", status.voice),
      testid: "agent-card-voice",
    },
    {
      key: "client",
      seq: 2,
      title: "Avatar Varázsló",
      description: "Építsd fel az ideális vásárlód teljes profilját",
      icon: UserIcon,
      to: "/agents/client",
      // Admin role A NORMÁL Műhely dashboardon UGYANAZT a progressziós
      // logikát látja, mint egy tester / user — az admin különbség csak
      // az Admin pulton + usage-limit-mentességen + agent visibility
      // kezelésen érvényes. Ha az adminnak nincs elmentett Márkahangja,
      // az Avatar kártya is ugyanúgy lockolt, mint egy újonnan belépő
      // teszternél.
      status: !status.voice?.saved ? "locked-prereq" : cardStatus("client", status.client),
      lockReason: "Először a Márkahang szükséges",
      testid: "agent-card-client",
    },
    {
      key: "offer",
      seq: 3,
      title: "Ajánlat Varázsló",
      description: "Erősebb ajánlat, könnyebb igen",
      icon: Target,
      to: "/agents/offer",
      status: !status.client?.saved ? "locked-prereq" : cardStatus("offer", status.offer),
      lockReason: "Először az Avatar szükséges",
      testid: "agent-card-offer",
    },
  ];

  const attractionAgents = [
    { key: "magnet-post", title: "Poszt Varázsló",         desc: "Görgetés megállító posztok, valódi beszélgetésért", icon: Newspaper,      to: "/agents/magnet-post" },
    { key: "email",       title: "Email Varázsló",         desc: "Emailek, amelyek közelebb hozzák a döntést",        icon: EnvelopeSimple, to: "/agents/email" },
    { key: "lead-magnet", title: "Ügyfélmágnes Varázsló",  desc: "Mini anyag egy gyors győzelemért",                  icon: DownloadSimple, to: "/agents/lead-magnet" },
  ];

  const conversionAgents = [
    { key: "webinar", title: "Webinár Varázsló",   desc: "Szerezz ügyfelet online előadással",     icon: MonitorPlay,  to: "/agents/webinar" },
    { key: "landing", title: "Landing Varázsló",   desc: "Hatékony ügyfélszerző oldalak",          icon: Layout,       to: "/agents/landing" },
    { key: "sales",   title: "Sales Mesterműhely", desc: "Értékesítő beszélgetések, nyomulás nélkül", icon: ChatsCircle,  to: "/agents/sales" },
  ];

  const savedCount = Object.values(status).filter((s) => s?.saved).length;
  // Műhely progress logika MINDEN role-ra egységes: a Vonzás / Konverzió
  // layer csak akkor nyílik meg, ha mind a 3 Foundation elem mentve van.
  // Admin role NEM bypass-olja a Műhely progressziót (csak az Admin pult,
  // limit-mentesség és visibility-kezelés admin-specifikus).
  const foundationComplete = savedCount === 3;

  return (
    <div className="p-8 md:p-12 max-w-[1400px] mx-auto relative mex-noise">
      {/* Header */}
      <div className="mb-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mex-eyebrow mb-2">Műhely</div>
          </div>
          <button
            type="button"
            onClick={toggleSound}
            data-testid="celebration-sound-toggle"
            aria-label={soundOn ? "Hangok kikapcsolása" : "Hangok bekapcsolása"}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] tracking-[0.18em] uppercase text-[#a1a1a1] hover:text-white hover:bg-white/[0.04] transition border border-white/[0.06]"
          >
            {soundOn ? (
              <>
                <SpeakerHigh size={13} color="#5e50f7" weight="fill" />
                <span className="text-[#5e50f7] font-medium">Hang</span>
              </>
            ) : (
              <>
                <SpeakerSlash size={13} />
                <span>Néma</span>
              </>
            )}
          </button>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-3">
          Üdv újra itt{
            (user?.first_name?.trim() || user?.name?.trim()?.split(" ")[0]) ? (
              <>
                ,{" "}
                <span className="text-[#5e50f7]" style={{ marginRight: "0.08em" }}>
                  {user.first_name?.trim() || user.name.trim().split(" ")[0]}
                </span>
              </>
            ) : null
          }!
        </h1>
        <p className="text-[#c3c3cc] text-[14px] max-w-2xl leading-relaxed">
          Építsd fel az alapokat, és alakítsd őket automatikus ügyfélszerző gépezetté... pár kattintással.
        </p>
      </div>

      {/* Foundation */}
      <Section
        color="#5e50f7"
        title="Alapozás"
        subtitle="Márkahang, avatar, ajánlat - a digitális DNS-ed."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {foundationAgents.map((a) => {
            const props = decorate(a.key, a);
            if (!props) return null;
            const { key: _k, ...cardProps } = props;
            return (
              <div
                key={a.key}
                ref={(el) => { cardRefs.current[a.key] = el; }}
                className="relative"
                style={{ overflow: "visible" }}
              >
                <AgentCard layer="foundation" {...cardProps} />
              </div>
            );
          })}
        </div>
      </Section>

      {/* Attraction */}
      <div id="attraction-section">
      <Section
        color="#75dff5"
        title="Vonzás"
        subtitle="Itt születnek az első érdeklődők."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {attractionAgents.map((a) => {
            const baseProps = {
              layer: "attraction",
              title: a.title,
              description: a.desc,
              icon: a.icon,
              to: a.to,
              status: foundationComplete ? undefined : "locked-prereq",
              lockReason: foundationComplete ? undefined : "Először töltsd ki az Alapozást",
              testid: `agent-card-attraction-${a.key}`,
            };
            const props = decorate(a.key, baseProps);
            if (!props) return null;
            return <AgentCard key={a.key} {...props} />;
          })}
        </div>
      </Section>
      </div>

      {/* Conversion */}
      <Section
        color="#ffdb5c"
        title="Konverzió"
        subtitle="Itt lesznek az érdeklődőkből az ügyfelek."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {conversionAgents.map((a) => {
            const baseProps = {
              layer: "conversion",
              title: a.title,
              description: a.desc,
              icon: a.icon,
              to: a.to,
              status: foundationComplete ? undefined : "locked-prereq",
              lockReason: foundationComplete ? undefined : "Először töltsd ki az Alapozást",
              testid: `agent-card-conversion-${a.key}`,
            };
            const props = decorate(a.key, baseProps);
            if (!props) return null;
            return <AgentCard key={a.key} {...props} />;
          })}
        </div>
      </Section>

      {/* Foundation save celebration — TA-TA-TAMMM cannon */}
      <CardConfettiCannon
        active={!!celebrateKey}
        targetRef={{ current: celebrateKey ? cardRefs.current[celebrateKey] : null }}
        onDone={() => setCelebrateKey(null)}
      />
    </div>
  );
}

function Section({ color, title, subtitle, children }) {
  return (
    <div className="mb-12">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-white mb-1">
            {title}
          </h2>
          <p className="text-[13px] text-[#a1a1a1]">{subtitle}</p>
        </div>
        <div className="hidden sm:block h-px flex-1 ml-8 mb-3" style={{ background: `${color}22` }} />
      </div>
      {children}
    </div>
  );
}

/**
 * PrereqBanner — replaces the Vonzás / Értékesítés card grids when Foundation
 * is incomplete. A single, calm "Először töltsd ki az Alapozást" call to
 * action, with the current progress count and a deep-link back to the
 * Foundation section above. Avoids the visual noise of three faded locked
 * cards per layer.
 */
function PrereqBanner({ savedCount }) {
  return (
    <div
      data-testid="upper-layer-prereq-banner"
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl px-6 py-5"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px dashed rgba(255,255,255,0.10)",
      }}
    >
      <div className="flex items-center gap-3">
        <LockSimple size={16} color="#9898ad" weight="duotone" />
        <div>
          <div className="text-[14px] text-[#d4d4e0] font-medium">
            Először töltsd ki az Alapozást.
          </div>
          <div className="text-[12px] text-[#9898ad] mt-0.5">
            Foundation mentve:{" "}
            <span className="text-[#7c6cf2] font-medium">{savedCount}/3</span>
            .
          </div>
        </div>
      </div>
      <a
        href="#top"
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        className="self-start sm:self-auto text-[12px] tracking-[0.18em] uppercase font-medium px-4 py-2 rounded-md transition"
        style={{
          color: "#7c6cf2",
          background: "rgba(94,80,247,0.08)",
          border: "1px solid rgba(94,80,247,0.30)",
        }}
        data-testid="upper-layer-prereq-cta"
      >
        Vissza az Alapozáshoz
      </a>
    </div>
  );
}
